import { X } from 'lucide-react';
import { METHOD_FIELDS } from '@higgins/m2q-pricing';
import type { SurfaceLine } from '@higgins/m2q-pricing';
import { NumberField } from './number-field';
import { RemoveButton } from './remove-button';
import { formatQuantity } from './surface-format';

/**
 * The line grid, shared with the surface card's column headings so they line up.
 * iPad landscape and up: number · a · b · qty · sides (only doors) · quantity · deduct · remove.
 * Narrower, iPad portrait included (the area list beside the card leaves too little room
 * for one row, #612): number · a · b · qty, with the rest on a second row (see LineRow).
 */
export const LINE_GRID =
  'grid grid-cols-[1.25rem_repeat(3,minmax(0,1fr))] gap-2 px-3 lg:grid-cols-[1.25rem_repeat(3,minmax(0,1fr))_auto_4.5rem_5.5rem_2.75rem]';

interface LineRowProps {
  method: string;
  line: SurfaceLine;
  index: number;
  quantity: number;
  onChange: (change: Partial<SurfaceLine>) => void;
  onRemove: () => void;
}

const SIDES = [1, 2] as const;

/**
 * A line with a measurement typed in asks before it goes; an empty one doesn't (#443).
 * Judged on `a` alone: every method has it, and a new line copies the previous `b` (addLine).
 */
const isMeasured = (line: SurfaceLine) => typeof line.a === 'number';

/** One measured line: the fields its method needs, the quantity it makes, deduct and remove. */
export function LineRow({ method, line, index, quantity, onChange, onRemove }: LineRowProps) {
  const fields = METHOD_FIELDS[method] ?? METHOD_FIELDS['Free (L × W × H)'];
  return (
    <div className={`${LINE_GRID} items-center py-1.5 ${line.deduct ? 'bg-red-50' : ''}`}>
      <span className="text-xs text-slate-500">{index + 1}</span>
      <NumberField label={fields.a} placeholder={fields.a} value={line.a} onChange={(a) => onChange({ a })} />
      {fields.b ? <NumberField label={fields.b} placeholder={fields.b} value={line.b} onChange={(b) => onChange({ b })} /> : <span />}
      <NumberField label="Qty" placeholder="Qty" value={line.qty} onChange={(qty) => onChange({ qty })} />
      {/* Below iPad landscape width these sit on their own row, right-aligned; from there up they are grid cells. */}
      <div className="col-span-3 col-start-2 flex items-center justify-end gap-2 lg:contents">
        {fields.sides ? (
          <div className="mr-auto flex gap-1 lg:mr-0" role="group" aria-label="Sides">
            {SIDES.map((sides) => (
              <button
                key={sides}
                type="button"
                onClick={() => onChange({ sides })}
                aria-label={`${sides} side${sides === 1 ? '' : 's'}`}
                className={`h-11 w-11 rounded-full border text-sm ${(line.sides ?? 1) === sides ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-600'}`}
              >
                {sides}
              </button>
            ))}
          </div>
        ) : (
          <span className="hidden lg:block" />
        )}
        <span className={`min-w-[4.5rem] text-right text-sm tabular-nums ${line.deduct ? 'text-red-700' : ''}`}>
          {line.deduct ? '−' : ''}
          {formatQuantity(quantity)}
        </span>
        <button
          type="button"
          onClick={() => onChange({ deduct: !line.deduct })}
          aria-pressed={!!line.deduct}
          className={`h-9 w-[5.5rem] rounded-full border text-xs ${line.deduct ? 'border-red-300 bg-white text-red-700' : 'border-slate-300 text-slate-500'}`}
        >
          deduct
        </button>
        <RemoveButton
          question={`Remove line ${index + 1}? Its measurement goes from this surface.`}
          ask={isMeasured(line)}
          onRemove={onRemove}
          label={`Remove line ${index + 1}`}
          className="flex h-11 w-11 items-center justify-center text-slate-400 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </RemoveButton>
      </div>
    </div>
  );
}
